import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export function Modal({ open, onClose, title, children, footer, testId }) {
  useEffect(() => {
    if (!open) return;
    const scrollY = window.scrollY;
    const body = document.body;
    const prev = {
      overflow: body.style.overflow,
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
    };
    // Lock background scroll (iOS-safe: fix body in place, restore on close)
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => {
      body.style.overflow = prev.overflow;
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      window.removeEventListener("keydown", onKey);
      window.scrollTo(0, scrollY);
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center sm:p-4"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative iks-card w-full sm:max-w-lg max-h-[90dvh] sm:max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl iks-pop"
        data-testid={testId}
      >
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-white/10 rounded-t-2xl bg-[#111827]">
          <h3 className="font-display font-semibold text-slate-100">{title}</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-100 transition-colors p-1 rounded-lg hover:bg-white/5"
            data-testid="modal-close-btn"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-5">{children}</div>
        {footer && (
          <div className="shrink-0 px-5 py-4 border-t border-white/10 flex gap-3 bg-[#111827] rounded-b-2xl">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block mb-4">
      {label && <span className="block text-sm font-medium text-slate-300 mb-1.5">{label}</span>}
      {children}
      {hint && <span className="block text-xs text-slate-500 mt-1">{hint}</span>}
    </label>
  );
}

export function TextInput(props) {
  return <input {...props} className={`iks-input w-full min-w-0 px-3.5 py-2.5 text-sm ${props.className || ""}`} />;
}

export function TextArea(props) {
  return (
    <textarea
      {...props}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.stopPropagation();
        props.onKeyDown?.(e);
      }}
      className={`iks-input w-full min-w-0 px-3.5 py-2.5 text-sm min-h-[90px] resize-y ${props.className || ""}`}
    />
  );
}
